import { Api } from './BaseApi'
import { IUser } from '../../types/User/IUser';
import { IStaff } from '../../types/IStaff';
import { ITeam } from '../../types/ITeam';
import { ILogOptions } from '../../types/User/ILogOptions';
import { IRole } from '../../types/User/IRole';
import { ISetting } from '../../types/User/ISetting';
import { IClientTypeFilter } from '../../types/User/IClientTypeFilter';
import { IContactResults, IContactSearchProps } from '../../types/User/IContact';
import { IMention } from '../../types/User/IMention';
import { IMentionConfig } from '../../types/User/IMentionConfig';
import { IClassification } from '../../types/User/IClassification';
import { IMonitor } from '../../types/User/IMonitor';

const UserApi = Api.injectEndpoints({
  endpoints: (build) => ({
    getUsersByIds: build.query<IUser[], string>({
      query: (Ids) => `User/ByIds/${Ids}`,
      providesTags: ["GetUsersByIds"],
    }),
    getUserById: build.query<IUser, number>({
      query: (id) => `User/ById/${id}`,
      providesTags: ["GetUsersById"],
    }),
    getStaff: build.query<IStaff[], { teamId?: number, includeQueues: boolean, includeStaffPlusForCompanyId: number }>({
      query: ({ teamId, includeQueues, includeStaffPlusForCompanyId }) => `User/Staff?teamId=${teamId ? teamId : ''}&includeQueues=${includeQueues}&includeStaffPlusForCompanyId=${includeStaffPlusForCompanyId}`,
      providesTags: ["UpdateStaff"],
    }),
    getMention: build.query<IMention[], { text: string, companyId: number | void }>({
      query: (prefix) => `User/Mention?prefix=${prefix.text}&companyIds=${prefix.companyId || ''}`,
    }),
    getMentionConfig: build.query<IMentionConfig, void>({
      query: () => `User/Mention/Config`,
    }),
    searchContacts: build.query<IStaff[], string>({
      query: (filter) => `User/Search?filter=${filter || ''}`,
      providesTags: ["SearchContacts"],
    }),
    getContacts: build.query<IContactResults, IContactSearchProps>({
      query: (props) => `User/${props.uid}/Contacts?favsOnly=${props.favsOnly || 'false'}&filter=${props.filter || ''}&pageLetter=${props.pageLetter || ''}&thisContactId=${props.thisContactId || ''}&groupByOffice=${props.groupByOffice ? 'true' : 'false'}`,
      providesTags: ["GetContacts"],
      //Only have one cache entry because the arg always maps to one string
      // serializeQueryArgs: ({ endpointName }) => {
      //   return endpointName;
      // },
    }),
    getUser: build.query<IUser, string | void>({
      query: (uid) => `User/${uid}`,
      providesTags: ["GetUser"],
    }),
    getUserMonitor: build.query<IMonitor[], void>({
      query: (uid) => `User/Me/Monitor`,
      providesTags: ["GetUserMonitor"],
    }),
    saveMonitor: build.mutation<unknown, IMonitor[]>({
      query: (setting) => ({
        url: `User/Me/Monitor`,
        method: "POST",
        body: setting,
      }),
      invalidatesTags: ["GetUserMonitor", "MyTickets"]
    }),
    debugTimezone: build.query<void, string>({
      query: (query) => `Debug/${query}`,
      providesTags: ["DebugTimezone"],
    }),
    getTeams: build.query<ITeam[], void>({
      query: () => "User/Team",
      providesTags: ["GetTeams"],
    }),
    getTeam: build.query<ITeam, number>({
      query: (Id: number) => `User/Team/${Id}`,
      providesTags: ["GetTeam"],
    }),
    getLogOptions: build.query<ILogOptions, void>({
      query: () => "User/Me/LogOptions",
      providesTags: ["MyLogOptions"],
    }),
    getRoles: build.query<IRole[], string>({
      query: (uid) => `User/${uid}/Role`,
      providesTags: ["UserRoles"],
    }),
    getSetting: build.query<ISetting, number>({
      query: (settingId) => `User/Setting/${settingId}/`,
      //providesTags: ["GetUserSetting"],
      providesTags: (result, error, id) => [{ type: 'GetUserSetting', id }],
    }),
    saveSetting: build.mutation<unknown, ISetting>({
      query: (setting) => ({
        url: `User/Setting/`,
        method: "POST",
        body: setting,
      }),
      //invalidatesTags: ["GetUserSetting"]
      invalidatesTags: (result, error, { Setting_Id }) => [{ type: 'GetUserSetting', id: Setting_Id }],
    }),
    getClientTypeFilters: build.query<IClientTypeFilter[], void>({
      query: (settingId) => `User/ClientTypeFilter`,
      providesTags: ["GetClientTypeFilter"],
    }),
    getUserClassification: build.query<IClassification, void>({
      query: () => `User/Classification`,
      providesTags: ["GetClassification"],
    }),
    postLogOptions: build.mutation<unknown, ILogOptions>({
      query: (options) => ({
        url: "User/Me/LogOptions",
        method: "POST",
        body: options,
      }),
      invalidatesTags: ["MyLogOptions"]
    }),
    postClientTypeFilter: build.mutation<unknown, IClientTypeFilter>({
      query: (filter) => ({
        url: "User/ClientTypeFilter",
        method: "POST",
        body: filter,
      }),
      invalidatesTags: ["GetClientTypeFilter", "GetMyClient"]
    }),
    dismissIntro: build.mutation<void, number>({
      query: (featureId) => ({
        url: `User/Me/DismissIntro/${featureId || 0}`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["UserProfile"]
    }),
    updateNotes: build.mutation<void, { uid: string, Notes: string }>({
      query: (props) => ({
        url: `User/${props.uid}/Notes`,
        method: "POST",
        body: props,
      }),
      invalidatesTags: ["UserProfile", "GetUsersById"]
    }),
  }),
  overrideExisting: false,
})

export { UserApi };